import { Collapsible } from '@ui/animated/Collapsible'
import Dropdown from '@ui/elements/Dropdown'
import { FormCheckbox } from '@ui/forms/Checkbox'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Platform, View } from 'react-native'
import { PrescriptionResponse } from '@ca/common'
import PrescriptionsModal, {
  PrescriptionButtonsProps,
} from '@order/modals/Prescriptions/PrescriptionsModal'

import ButtonsFixed from '@ui/buttons/ButtonsFixed'
import { RefractionForm, refractionFormSchema } from '@ca/common'
import Page from '@shell/Page'
import FormInput from '@ui/forms/Input/FormInput'
import FormSelect from '@ui/forms/Select/FormSelect'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  addResultListener,
  BrillenSdkButtons,
  prepare,
} from '@modules/brillen-sdk'
import { useOrderState } from '@order/hooks/useOrderState'
import { trpc } from 'api'
import { router } from 'expo-router'
import Analytics from 'hooks/analytics/Analytics'
import { useCustomer } from 'hooks/useSession'
import { useStaffList } from 'hooks/useStaffList'
import { FormProvider, useForm, useWatch } from 'react-hook-form'
import { Label, SelectField } from './components'
import { PrismsPd } from './PrismPd'
import PickerButton from './RefractionPicker/PickerButton'
import PickerModal from './RefractionPicker/PickerModal'
import { SelectedKey } from './RefractionPicker/types'
import { GAP, WIDTH, sButtonWrapper, sContainer, sRow } from './styles'
import MeasurementUtils from 'utils/MeasurementUtils'
import Header from '@ui/elements/Header'
import { MeasurementLocation } from 'hooks/measurements/useMeasureSdk'
import { useMeasure } from 'features/measurements/useMeasure'

export const defaultValues = {
  l_sph: '',
  r_sph: '',
  r_cyl: '',
  l_cyl: '',
  r_axis: '',
  l_axis: '',
  r_add: '',
  l_add: '',
  r_visus: '',
  l_visus: '',
  r_pd: '',
  l_pd: '',
  remark: '',
  prism: false,
  r_prism1: '',
  l_prism1: '',
  r_prism2: '',
  l_prism2: '',
  right_na_te: 'in',
  left_na_te: 'in',
  right_ha_ba: 'up',
  left_ha_ba: 'up',
  optometrist: null as null | number,
}

const prefixes = {
  r: ['r_', 'right_'],
  l: ['l_', 'left_'],
}

const PrescriptionFormButtons = ({
  prescriptionLocal,
  onClose,
  onConfirm,
}: PrescriptionButtonsProps) => {
  const { t } = useTranslation(['ui'])
  return (
    <ButtonsFixed.Buttons
      disableTransitions
      buttons={[
        {
          variant: 'outlined',
          label: t('ui:cancel'),
          onPress: onClose,
        },
        {
          variant: prescriptionLocal ? 'filled' : 'outlined',
          label: t('ui:prescription.fillForm'),
          iconEnd: 'arrow-narrow-right',
          onPress: onConfirm,
        },
      ]}
    />
  )
}

export default function PrescriptionForm() {
  const { t } = useTranslation(['ui', 'common', 'prescription', 'vision'])
  const text = 'vision:prescription.manual'
  const [selected, setSelected] = useState<SelectedKey<RefractionForm>>(null)
  const [fillModalOpen, setFillModalOpen] = useState(false)
  const customer = useCustomer()
  const form = useForm<typeof defaultValues, any, typeof defaultValues>({
    defaultValues,
    // react-hook-form ≥7.55: zodResolver infers z.input<schema> as TFieldValues, mismatching
    // typeof defaultValues (strings vs narrowed unions). `as any` preserves pre-7.55 behavior —
    // Zod still validates and transforms at runtime. See docs/expo54-migration.md § Known Issues.
    resolver: zodResolver(refractionFormSchema) as any,
  })

  const staff = useStaffList()

  const prism = useWatch({ control: form.control, name: 'prism' })
  const r_add = useWatch({ control: form.control, name: 'r_add' })
  const l_add = useWatch({ control: form.control, name: 'l_add' })

  const setPrescriptionId = useOrderState((s) => s.setPrescriptionId)

  const utils = trpc.useUtils()
  const { onMeasure: onMeasurePD } = useMeasure('pd', {
    location: MeasurementLocation.prescription,
  })

  const save = trpc.customer.prescriptions.create.useMutation({
    onSuccess: (res) => {
      utils.customer.prescriptions.list.invalidate()
      if (customer?.id) {
        utils.customer.prescriptions.byId.setData(
          { prescriptionId: res.id, customerId: customer.id },
          res
        )
      }
      setPrescriptionId(res.id)
      Analytics.logEvent('prescription_external', {
        prescriptionId: `${res.id}`,
        optometrist: res.optometrist_name || '',
      })
      router.navigate('/vision/values')
    },
  })

  const create = form.handleSubmit((values) => {
    // handleSubmit types values as typeof defaultValues (strings) because TTransformedValues = TFieldValues.
    // At runtime Zod has already validated and transformed the values to RefractionForm shape.
    save.mutate({
      data: values as unknown as RefractionForm,
      customerId: `${customer?.id}`,
    })
  })

  const copyValues = useCallback(
    (from: 'r' | 'l') => {
      const values = form.getValues()
      const prefixesFrom = prefixes[from]
      const prefixesTo = prefixes[from === 'r' ? 'l' : 'r']
      Object.entries(values).forEach(([key, value]) => {
        const prefixIdx = prefixesFrom.findIndex((prefix) =>
          key.startsWith(prefix)
        )
        if (prefixIdx === -1) {
          return
        }
        const prefixTo = prefixesTo[prefixIdx]
        const prefixForm = prefixesFrom[prefixIdx]
        const otherKey = key.replace(
          prefixForm,
          prefixTo
        ) as keyof typeof defaultValues
        if (value === '') {
          return
        }
        form.setValue(otherKey, value)
      })
    },
    [form]
  )

  const fillFromPrescription = useCallback(
    (prescription: PrescriptionResponse) => {
      form.reset({
        r_sph: String(prescription.r_sph),
        l_sph: String(prescription.l_sph),
        r_cyl: String(prescription.r_cyl),
        l_cyl: String(prescription.l_cyl),
        r_axis: String(prescription.r_axis),
        l_axis: String(prescription.l_axis),
        r_add: String(prescription.r_add),
        l_add: String(prescription.l_add),
        r_visus: String(prescription.r_visus),
        l_visus: String(prescription.l_visus),
        r_pd: String(prescription.r_pd),
        l_pd: String(prescription.l_pd),
        prism: prescription.prism,
        r_prism1: String(prescription.r_prism1),
        l_prism1: String(prescription.l_prism1),
        r_prism2: String(prescription.r_prism2),
        l_prism2: String(prescription.l_prism2),
        right_na_te: prescription.right_na_te ?? 'in',
        left_na_te: prescription.left_na_te ?? 'in',
        right_ha_ba: prescription.right_ha_ba ?? 'up',
        left_ha_ba: prescription.left_ha_ba ?? 'up',
        remark: prescription.remark ?? '',
      })
    },
    [form]
  )

  const optometristOptions = useMemo(() => {
    if (!staff.data) {
      return []
    }
    return staff.data.map(({ id, name }) => ({
      value: id,
      label: name,
    }))
  }, [staff])

  const tools = (
    <Dropdown
      width={360}
      icon={{ name: 'dots' }}
      popoverProps={{ position: ['bottom', 'left'], offset: [0, 18] }}
      items={[
        {
          label: t('ui:prescription.fillFromPrevious'),
          icon: 'clipboard',
          onPress() {
            setFillModalOpen(true)
          },
        },
        {
          label: t('ui:prescription.copyRtoL'),
          icon: 'square-arrow-down',
          onPress() {
            copyValues('r')
          },
        },
        {
          label: t('ui:prescription.copyLtoR'),
          icon: 'square-arrow-up',
          onPress() {
            copyValues('l')
          },
        },
        {
          label: t('ui:prescription.measurePD'),
          icon: 'pd-filled',
          onPress() {
            onMeasurePD()
          },
          hidden: Platform.OS === 'web',
        },
        {
          label: t('ui:reset'),
          icon: 'ban',
          destructive: true,
          onPress() {
            form.reset()
          },
        },
      ]}
    />
  )

  useEffect(() => {
    const sub = addResultListener((e) => {
      if (e.button !== BrillenSdkButtons.save) {
        return
      }
      const { left, right } = MeasurementUtils.parse.pd.result(e)
      form.setValue('r_pd', `${right}`)
      form.setValue('l_pd', `${left}`)
    })
    prepare()
    return () => {
      sub?.remove()
    }
  }, [])

  // r_add controls l_add
  useEffect(() => {
    if (l_add === r_add) return
    form.setValue('l_add', r_add)
  }, [r_add])

  // l_add controls r_add
  useEffect(() => {
    if (l_add === r_add) return
    form.setValue('r_add', l_add)
  }, [l_add])

  const pickerProps = {
    control: form.control,
    selected,
    setSelected,
  }

  return (
    <>
      <Page stage="vision" validateStaff>
        <Header
          title={t(`${text}.header.title`)}
          desc={t(`${text}.header.desc`)}
        />
        <FormProvider {...form}>
          <View className={sContainer}>
            <View style={{ gap: GAP }}>
              <View className={sRow}>
                <View className={sButtonWrapper}>{tools}</View>
                <Label>{t('prescription:sph', 'SPH')}</Label>
                <Label>{t('prescription:cyl', 'CYL')}</Label>
                <Label>{t('prescription:axis', 'AXIS')}</Label>
                <Label>{t('prescription:add', 'ADD')}</Label>
                <Label>{t('prescription:visus', 'VISUS')}</Label>
                <Label>{t('prescription:pd', 'PD')}</Label>
              </View>
              <View className={sRow}>
                <View className={sButtonWrapper}>
                  <Label>R</Label>
                </View>
                <PickerButton {...pickerProps} name="r_sph" />
                <PickerButton {...pickerProps} name="r_cyl" />
                <PickerButton {...pickerProps} name="r_axis" />
                <PickerButton {...pickerProps} name="r_add" />
                <PickerButton {...pickerProps} name="r_visus" />
                <PickerButton {...pickerProps} name="r_pd" />
              </View>
              <View className={sRow}>
                <View className={sButtonWrapper}>
                  <Label>{t('prescription:l', 'L')}</Label>
                </View>
                <PickerButton {...pickerProps} name="l_sph" />
                <PickerButton {...pickerProps} name="l_cyl" />
                <PickerButton {...pickerProps} name="l_axis" />
                <PickerButton {...pickerProps} name="l_add" />
                <PickerButton {...pickerProps} name="l_visus" />
                <PickerButton {...pickerProps} name="l_pd" />
              </View>
            </View>
            <View>
              <FormCheckbox
                control={form.control}
                label={t('prescription:prism', 'Prism')}
                name="prism"
              />
              <Collapsible open={prism} fullWidth>
                <View style={{ gap: GAP, paddingTop: GAP }}>
                  <View className={sRow}>
                    <View className={sButtonWrapper}></View>
                    <Label width={2 * WIDTH + GAP}>
                      {t('prescription:horizontal', 'HORIZONTAL')}
                    </Label>
                    <Label width={2 * WIDTH + GAP}>
                      {t('prescription:vertical', 'VERTICAL')}
                    </Label>
                    {/* <Label>BASE</Label>
                  <Label>PRISM</Label> */}
                    <Label>{t('prescription:prism_pd', 'PRISM PD')}</Label>
                  </View>
                  <View className={sRow} style={{ gap: GAP }}>
                    <View className={sButtonWrapper}>
                      <Label>{t('prescription:r', 'R')}</Label>
                    </View>
                    <SelectField
                      control={form.control}
                      name="right_na_te"
                      options={[
                        { value: 'in', label: t('prescription:in') },
                        { value: 'out', label: t('prescription:out') },
                      ]}
                    />
                    <PickerButton {...pickerProps} name="r_prism1" />
                    <SelectField
                      name="right_ha_ba"
                      control={form.control}
                      options={[
                        { value: 'up', label: t('prescription:up') },
                        { value: 'down', label: t('prescription:down') },
                      ]}
                    />
                    <PickerButton {...pickerProps} name="r_prism2" />
                    {/* <Base lens="r" />
                  <Prism lens="r" /> */}
                    <PrismsPd lens="r" />
                  </View>
                  <View className={sRow} style={{ gap: GAP }}>
                    <View className={sButtonWrapper}>
                      <Label>{t('prescription:l', 'L')}</Label>
                    </View>
                    <SelectField
                      control={form.control}
                      name="left_na_te"
                      options={[
                        { value: 'in', label: t('prescription:in') },
                        { value: 'out', label: t('prescription:out') },
                      ]}
                    />
                    <PickerButton {...pickerProps} name="l_prism1" />
                    <SelectField
                      name="left_ha_ba"
                      control={form.control}
                      options={[
                        { value: 'up', label: t('prescription:up') },
                        { value: 'down', label: t('prescription:down') },
                      ]}
                    />
                    <PickerButton {...pickerProps} name="l_prism2" />
                    {/* <Base lens="l" />
                  <Prism lens="l" /> */}
                    <PrismsPd lens="l" />
                  </View>
                </View>
              </Collapsible>
            </View>
            <View style={{ gap: GAP }}>
              <FormSelect
                control={form.control}
                name="optometrist"
                isLoading={staff.isPending}
                options={optometristOptions}
                label={t('common:optician')}
              />
              <FormInput
                multiline
                control={form.control}
                name="remark"
                placeholder={t('ui:message')}
                className="h-36"
              />
            </View>
          </View>
          <PickerModal {...pickerProps} />
        </FormProvider>
      </Page>
      <ButtonsFixed
        buttons={[
          {
            variant: 'filled',
            label: t('ui:create'),
            onPress: create,
            iconEnd: 'plus',
          },
        ]}
      />
      <PrescriptionsModal
        open={fillModalOpen}
        close={() => setFillModalOpen(false)}
        onSelect={fillFromPrescription}
        Buttons={PrescriptionFormButtons}
      />
    </>
  )
}
